/**
 * The editor's left-hand pane: the snapshot library as a folder tree.
 *
 * Everything here is derived from the files themselves — folders come from each
 * snapshot's `metadata.exportFolder` (see modules/snapshotTree), so there
 * is no hierarchy document, no per-item mongo record and nothing to keep in
 * sync. Dragging a snapshot onto a folder writes that field; the tree follows.
 *
 * The one thing that cannot be derived is an empty folder, so "New folder" keeps
 * its path here in component state — a draft, live only until something is
 * dropped into it. A draft the user leaves empty is gone on the next page load,
 * which is the right outcome: nothing on disk ever referred to it.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Tree } from "@minoru/react-dnd-treeview";
import { SNAPSHOT_DRAG_TYPE, setDraggedSnapshot } from "../../modules/snapshotAgentPromptTokens";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import RefreshIcon from "@material-ui/icons/Refresh";
import CreateNewFolderIcon from "@material-ui/icons/CreateNewFolder";
import NoteAddIcon from "@material-ui/icons/NoteAdd";
import SystemUpdateAltIcon from "@material-ui/icons/SystemUpdateAlt";
import PublishIcon from "@material-ui/icons/Publish";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import FolderIcon from "@material-ui/icons/Folder";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import DescriptionIcon from "@material-ui/icons/Description";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import LockIcon from "@material-ui/icons/Lock";
import moment from "moment";
import {
  applyFolderRename,
  buildSnapshotTree,
  fileNameFromNodeId,
  folderNodeId,
  folderPathFromNodeId,
  isInsideFolder,
  normalizeFolderPath,
  ROOT_ID,
} from "../../modules/snapshotTree";
import {
  BAR_BG,
  CONTROL_BG,
  FIELD_LABEL,
  PANEL_BG,
  SECTION_DIVIDER,
} from "./panelPalette";
import {
  readSnapshotFromFile,
  snapshotStemFromFileName,
} from "../../modules/snapshotFileTransfer";
import DialogDeliveryTask from "./dialogBuildSubmit";
import DrawerJobs, { JobsButton } from "./drawerJobs";
import DialogExportAsVersion from "./dialogExportAsVersion";
import DialogImportVersion from "./dialogImportVersion";

// The row highlights of the version editor's component tree, so the two trees
// on either side of the preview behave the same way under the pointer.
const ROW_HOVER = "rgba(255, 255, 255, 0.055)";
const ROW_SELECTED = "rgba(144, 202, 249, 0.20)";
const ROW_SELECTED_HOVER = "rgba(144, 202, 249, 0.26)";

// Shared by the column header and every tree row so Name / Updated line up.
// action column = two MUI small IconButton footprints so reload + menu fit while
// the header spacer still matches rows.
const ROW_GRID = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) 72px 68px",
  columnGap: "6px",
  alignItems: "center",
  paddingLeft: 6,
  paddingRight: 6,
};

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minWidth: 0,
    overflow: "hidden",
    backgroundColor: PANEL_BG,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 8px",
    backgroundColor: BAR_BG,
    borderBottom: `1px solid ${SECTION_DIVIDER}`,
  },
  filter: { flex: 1 },
  filterInput: {
    color: "#eee",
    fontSize: "13px",
    backgroundColor: CONTROL_BG,
    borderRadius: 4,
    padding: "2px 6px",
  },
  sortButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 0,
    margin: 0,
    padding: 0,
    border: "none",
    background: "transparent",
    color: FIELD_LABEL,
    fontSize: "12px",
    fontFamily: "inherit",
    cursor: "pointer",
    lineHeight: 1.2,
    "&:hover": { color: "#eee" },
  },
  sortButtonActive: { color: "#eee" },
  sortButtonUpdated: {
    justifyContent: "flex-end",
    width: "100%",
  },
  sortIcon: {
    fontSize: "18px",
    marginLeft: -2,
    opacity: 0.85,
  },
  treeScroll: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    padding: "4px 4px 8px 4px",
    // react-dnd-treeview wraps rows in ul/li — strip their chrome so the shared
    // grid lines up with the sticky Name / Updated header above.
    "& ul": { margin: 0, padding: 0, listStyle: "none", width: "100%" },
    "& li": { listStyle: "none", width: "100%" },
  },
  // Same grid as each tree row (name | updated | menu) so columns line up.
  columnHeader: {
    ...ROW_GRID,
    width: "100%",
    boxSizing: "border-box",
    paddingTop: 4,
    paddingBottom: 4,
    marginBottom: 2,
    borderBottom: `1px solid ${SECTION_DIVIDER}`,
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: PANEL_BG,
  },
  row: {
    ...ROW_GRID,
    width: "100%",
    boxSizing: "border-box",
    paddingTop: 3,
    paddingBottom: 3,
    borderRadius: "4px",
    cursor: "pointer",
    userSelect: "none",
    "&:hover": { backgroundColor: ROW_HOVER },
  },
  rowSelected: {
    backgroundColor: ROW_SELECTED,
    "&:hover": { backgroundColor: ROW_SELECTED_HOVER },
  },
  rowUnreadable: { opacity: 0.45, cursor: "not-allowed" },
  rowMain: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  label: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "13px",
    color: "#eee",
  },
  meta: { fontSize: "11px", color: "#8a8d93", whiteSpace: "nowrap" },
  updated: {
    fontSize: "11px",
    color: "#8a8d93",
    whiteSpace: "nowrap",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  menuSlot: {
    width: 68,
    height: 34,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  icon: { fontSize: "16px", color: "#8a8a8a", flexShrink: 0 },
  lockIcon: { fontSize: "14px", color: "#c8a45c", flexShrink: 0, marginLeft: "2px" },
  // Stays until the folder is opened, unlike the snackbar.
  jobSpinner: { flexShrink: 0, marginLeft: "4px", color: "#8ab4c8" },
  jobDot: { width: "8px", height: "8px", borderRadius: "50%", flexShrink: 0, marginLeft: "5px" },
  jobDotSuccess: { backgroundColor: "#7cc47f" },
  jobDotWarning: { backgroundColor: "#d8a657" },
  jobDotError: { backgroundColor: "#e57373" },
  empty: { padding: "16px 10px", color: "#8a8d93", fontSize: "13px" },
}));

// One input for the whole tree; the header button clicks it.
const ADD_SNAPSHOT_INPUT_ID = "snapshot-library-add-input";

const MENU_PAPER_PROPS = { style: { backgroundColor: BAR_BG, color: FIELD_LABEL } };
const DIALOG_PAPER_PROPS = { style: { backgroundColor: PANEL_BG } };

/** Short date for the Updated column; em dash when the FS has no mtime. */
function formatUpdated(mtimeMs) {
  if (!mtimeMs) return "—";
  return moment(mtimeMs).format("DD.MM.YY");
}

export default function ContainerSnapshotList({ editor, createSnackbar }) {
  const classes = useStyles();
  const [filterTerm, setFilterTerm] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [downloadingFolderPath, setDownloadingFolderPath] = useState(null);
  const [menu, setMenu] = useState(null); // { anchorEl, node }
  const [prompt, setPrompt] = useState(null); // { kind, node, value }
  // The folder a delivery task dialog is open for, or null. A folder is one
  // Asana task, so the action belongs to the folder row rather than a toolbar.
  const [taskFolderPath, setTaskFolderPath] = useState(null);
  // Whether the background-task drawer is open. The jobs themselves live in the
  // editor, so this is only the list's own panel.
  const [isJobsOpen, setIsJobsOpen] = useState(false);
  const [selectedForExport, setSelectedForExport] = useState([]);
  const [versionFileNames, setVersionFileNames] = useState(null);
  // Whether the "import a version as a snapshot" dialog is open. Unlike the
  // export dialog it creates a new snapshot rather than acting on a selection.
  const [isImportVersionOpen, setIsImportVersionOpen] = useState(false);
  // Importing a version is gated on exportPlayable, the same permission as every
  // other way a playable leaves or enters this pool. The server refuses it
  // regardless; this only keeps a button that cannot work off the toolbar.
  const canImportVersion = useMemo(
    () => (localStorage.getItem("user.permissions") || "").split(",").indexOf("exportPlayable") !== -1,
    []
  );
  const [draftFolders, setDraftFolders] = useState([]); // folder paths with no file in them yet
  const [sortBy, setSortBy] = useState("name"); // "name" | "updated"
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"

  const treeData = useMemo(
    () => buildSnapshotTree(editor.snapshots, filterTerm, draftFolders, { sortBy, sortDir }),
    [draftFolders, editor.snapshots, filterTerm, sortBy, sortDir]
  );

  // Selection survives filtering and collapsing, but discarded files do not.
  useEffect(() => {
    const available = new Set((editor.snapshots || []).map((item) => item.fileName));
    setSelectedForExport((current) => {
      const kept = current.filter((name) => available.has(name));
      return kept.length === current.length ? current : kept;
    });
  }, [editor.snapshots]);

  const selectedNames = useMemo(() => new Set(selectedForExport), [selectedForExport]);

  function namesInFolder(folderPath) {
    return (editor.snapshots || [])
      .filter((item) => isInsideFolder(normalizeFolderPath(item.exportFolder), folderPath))
      .filter((item) => item.isReadable !== false)
      .map((item) => item.fileName);
  }

  function toggleExportSelection(node) {
    const names = node.droppable
      ? namesInFolder(folderPathFromNodeId(node.id))
      : [fileNameFromNodeId(node.id)];
    setSelectedForExport((current) => {
      const next = new Set(current);
      const shouldSelect = names.some((name) => !next.has(name));
      names.forEach((name) => (shouldSelect ? next.add(name) : next.delete(name)));
      return Array.from(next);
    });
  }

  function toggleSort(column) {
    if (sortBy === column) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(column);
    // Updated defaults to newest-first; Name to A→Z.
    setSortDir(column === "updated" ? "desc" : "asc");
  }

  /**
   * Refuse a change to a folder that is being built or delivered right now.
   *
   * Those jobs run in the agent container and no longer hold the dialog open,
   * so the tree is reachable while one is going. The build reads these very
   * files and stages them under their delivery names: renaming the folder
   * mid-run, or moving a file out of it, would ship something nobody approved.
   * The container's own per-task lock cannot see any of this — it locks the
   * task, not the source files.
   *
   * @returns true when the caller must not proceed.
   */
  function refuseWhileFolderBusy(folderPath) {
    if (!editor.jobs.isScopeBusy(normalizeFolderPath(folderPath || ""))) return false;
    createSnackbar?.(
      "This folder is being built or submitted — wait for that to finish.",
      "warning",
      false
    );
    return true;
  }

  /** The folder a snapshot currently sits in, as the jobs name it. */
  function folderOfSnapshot(fileName) {
    const snapshot = (editor.snapshots || []).find((item) => item.fileName === fileName);
    return normalizeFolderPath(snapshot ? snapshot.exportFolder || "" : "");
  }

  async function refresh() {
    setIsRefreshing(true);
    try {
      await editor.refreshSnapshots();
    } finally {
      setIsRefreshing(false);
    }
  }

  /**
   * Dropping a FOLDER into another folder is a rename: a folder is not a thing,
   * only a value its files share, so nesting one inside another means rewriting
   * that value — "batch" dropped on "brand" becomes "brand/batch", and every
   * snapshot under it follows, nested folders included.
   */
  async function dropFolder(dragSource, dropTargetId) {
    const from = folderPathFromNodeId(dragSource.id);
    const into = folderPathFromNodeId(dropTargetId);
    if (from === null || into === null) return;
    // Into itself, into its own child, or back where it already is.
    if (isInsideFolder(into, from)) return;

    const name = from.split("/").pop();
    const to = into ? `${into}/${name}` : name;
    if (to === from) return;

    if (refuseWhileFolderBusy(from)) return;
    if (refuseWhileFolderBusy(into)) return;

    renameDraftFolders(from, to);
    // A draft holds no files, so there is nothing on disk to rewrite.
    if (dragSource.data?.isDraft) return;
    await editor.mutateSnapshot("renameFolder", { folderPath: from, newFolderPath: to });
  }

  /**
   * Dropping a snapshot on a folder writes that folder into the file's
   * `metadata.exportFolder`; the tree is derived from that field, so it follows
   * on the next listing.
   */
  async function handleDrop(_tree, { dragSource, dragSourceId, dropTargetId }) {
    if (dragSource && dragSource.droppable) {
      await dropFolder(dragSource, dropTargetId);
      return;
    }

    const fileName = fileNameFromNodeId(dragSourceId);
    const folderPath = folderPathFromNodeId(dropTargetId);
    if (!fileName || folderPath === null) return;
    // Both ends: the folder losing the file is as mid-build as the one gaining it.
    if (refuseWhileFolderBusy(folderOfSnapshot(fileName))) return;
    if (refuseWhileFolderBusy(folderPath)) return;

    const result = await editor.mutateSnapshot("setFolder", { fileName, folderPath });

    // A file now names this folder, so the draft that stood in for it until the
    // first drop has done its job and the listing carries it from here.
    if (result && folderPath) {
      setDraftFolders((current) => current.filter((path) => path !== folderPath));
    }
  }

  /** Add a draft, ignoring one that is already in the tree either way. */
  function addDraftFolder(folderPath) {
    const normalized = normalizeFolderPath(folderPath);
    if (!normalized) return;
    setDraftFolders((current) =>
      current.includes(normalized) ? current : [...current, normalized]
    );
  }

  /**
   * Carry a folder rename over to the drafts. Renaming a real folder has to do
   * this too: a draft nested inside it would otherwise be left pointing at a
   * name that no longer exists.
   */
  function renameDraftFolders(from, to) {
    setDraftFolders((current) =>
      Array.from(
        new Set(current.map((path) => applyFolderRename(path, from, to)).filter(Boolean))
      )
    );
  }

  function removeDraftFolder(folderPath) {
    setMenu(null);
    setDraftFolders((current) => current.filter((path) => !isInsideFolder(path, folderPath)));
  }

  /**
   * Freeze or unfreeze one snapshot. The flag is written into the file's own
   * `metadata.locked`, so the agent sees it too and the server refuses writes
   * to it — the read-only editor is the convenience, not the guarantee.
   */
  async function setLock(node) {
    setMenu(null);
    const fileName = fileNameFromNodeId(node?.id);
    if (!fileName) return;
    await editor.mutateSnapshot("setLock", { fileName, locked: !node?.data?.locked });
  }

  /** The same, for every snapshot under a folder — nested folders included. */
  async function setFolderLock(node) {
    setMenu(null);
    const folderPath = folderPathFromNodeId(node?.id);
    if (folderPath === null) return;
    await editor.mutateSnapshot("setFolderLock", { folderPath, locked: !node?.data?.locked });
  }

  async function reloadSnapshot(node) {
    const fileName = fileNameFromNodeId(node?.id);
    if (!fileName) return;

    const wasSelected = fileName === editor.selectedFileName;
    await editor.reloadSnapshot(fileName);

    // A selected snapshot keeps the same iframe key while it is re-read. Ask
    // the preview to boot once with the newly fetched snapshot. Selecting a
    // different row already changes the iframe key, so it boots by itself.
    if (wasSelected) {
      window.dispatchEvent(new Event("playengine:agent-reload"));
    }
  }

  /**
   * Read a chosen file and open the name dialog on it.
   *
   * The snapshot is held in the prompt rather than posted straight away: the
   * library is keyed by file name, and the one the user picked off their disk is
   * a suggestion — it may collide, or carry characters the library will not take.
   */
  async function chooseSnapshotFile(file) {
    if (!file) return;
    try {
      const snapshot = await readSnapshotFromFile(file);
      setPrompt({
        kind: "create",
        node: null,
        value: snapshotStemFromFileName(file.name),
        snapshot,
      });
    } catch (err) {
      createSnackbar?.(err.message, "error", false);
    }
  }

  function openPrompt(kind, node = null) {
    setMenu(null);
    const fileName = node ? fileNameFromNodeId(node.id) : null;
    const stem = fileName ? fileName.replace(/\.json$/i, "") : "";
    const defaults = {
      // Stem only — `.json` is appended on submit so the user cannot break the ext.
      rename: stem,
      duplicate: stem ? `${stem}-copy` : "",
      renameFolder: (node && folderPathFromNodeId(node.id)) || "",
      // Asked from a folder's menu, the new one belongs inside it: the path is
      // seeded so only the name is left to type.
      createFolder: node && node.droppable ? `${folderPathFromNodeId(node.id)}/` : "",
      create: "",
    };
    setPrompt({ kind, node, value: defaults[kind] ?? "" });
  }

  /** Strip any extension the user typed; always deliver a `.json` name to the API. */
  function snapshotFileNameFromStem(raw) {
    const stem = String(raw || "")
      .trim()
      .replace(/\.json$/i, "");
    return stem ? `${stem}.json` : "";
  }

  async function submitPrompt() {
    const { kind, node, snapshot, value } = prompt;
    setPrompt(null);

    if (kind === "createFolder") {
      addDraftFolder(value);
      return;
    }

    if (kind === "create") {
      await editor.mutateSnapshot("create", {
        newFileName: snapshotFileNameFromStem(value),
        snapshot,
      });
      return;
    }

    const fileName = fileNameFromNodeId(node.id);
    if (kind === "rename") {
      await editor.mutateSnapshot("rename", {
        fileName,
        newFileName: snapshotFileNameFromStem(value),
      });
    } else if (kind === "duplicate") {
      await editor.mutateSnapshot("duplicate", {
        fileName,
        newFileName: snapshotFileNameFromStem(value),
      });
    } else if (kind === "renameFolder") {
      const folderPath = folderPathFromNodeId(node.id);
      if (refuseWhileFolderBusy(folderPath)) return;
      const newFolderPath = normalizeFolderPath(value);
      renameDraftFolders(folderPath, newFolderPath);
      // A draft holds no files, so there is nothing on disk to rewrite.
      if (!node.data?.isDraft) {
        await editor.mutateSnapshot("renameFolder", { folderPath, newFolderPath });
      }
    }
  }

  function requestDiscard(node) {
    setMenu(null);
    setPrompt({ kind: "discard", node, value: "" });
  }

  async function confirmDiscard() {
    const node = prompt?.node;
    setPrompt(null);
    if (!node) return;

    const fileName = fileNameFromNodeId(node.id);
    if (refuseWhileFolderBusy(folderOfSnapshot(fileName))) return;
    await editor.mutateSnapshot("discard", { fileName });
  }

  const PROMPT_LABELS = {
    create: {
      title: "Add snapshot",
      label: "Name",
      help: "Saved into the snapshot folder as a .json file, exactly as it is.",
    },
    rename: {
      title: "Rename snapshot",
      label: "Name",
      help: "Saved as a .json file — you do not need to type the extension.",
    },
    duplicate: {
      title: "Duplicate snapshot",
      label: "Name",
      help: "Saved as a .json file — you do not need to type the extension.",
    },
    renameFolder: {
      title: "Rename folder",
      label: "Folder path",
      help: "Empty moves everything in it to the root.",
    },
    createFolder: {
      title: "New folder",
      label: "Folder name",
      help: "Use / to nest. It only sticks once a snapshot is dropped into it.",
    },
  };

  function renderSortLabel(column, label, alignEnd) {
    const isActive = sortBy === column;
    const SortIcon = sortDir === "asc" ? ArrowDropUpIcon : ArrowDropDownIcon;
    return (
      <button
        type="button"
        className={[
          classes.sortButton,
          isActive ? classes.sortButtonActive : "",
          alignEnd ? classes.sortButtonUpdated : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={() => toggleSort(column)}
        title={
          isActive
            ? `Sorted ${sortDir === "asc" ? "ascending" : "descending"} — click to reverse`
            : `Sort by ${label}`
        }
      >
        <span>{label}</span>
        {isActive && <SortIcon className={classes.sortIcon} />}
      </button>
    );
  }

  // The folder being looked at on its own, as a Finder window is: double-click
  // a folder to step into it, the ".." row or the path bar steps back out. The
  // tree's own open/shut is untouched; this only changes where it starts.
  const [cwd, setCwd] = useState(ROOT_ID);
  const cwdPath = folderPathFromNodeId(cwd);
  // A folder renamed, deleted or emptied away must not strand the list.
  useEffect(() => {
    if (cwd !== ROOT_ID && !treeData.some((node) => node.id === cwd)) setCwd(ROOT_ID);
  }, [treeData, cwd]);
  const trail = cwdPath ? cwdPath.split("/").map((name, position, all) => ({
    name,
    id: folderNodeId(all.slice(0, position + 1).join("/")),
  })) : [];
  const parentOfCwd = trail.length > 1 ? trail[trail.length - 2].id : ROOT_ID;

  function renderNode(node, { depth, isOpen, onToggle }) {
    const isFolder = Boolean(node.droppable);
    const fileName = fileNameFromNodeId(node.id);
    // A build or a delivery is about a folder; an export can be about either.
    const jobStatus = editor.jobs.rowStatus(
      isFolder ? folderPathFromNodeId(node.id) : fileName
    );
    const isSelected = !isFolder && fileName === editor.selectedFileName;
    const isUnreadable = !isFolder && node.data && node.data.isReadable === false;
    const folderNames = isFolder ? namesInFolder(folderPathFromNodeId(node.id)) : [];
    const checkedCount = folderNames.filter((name) => selectedNames.has(name)).length;

    const rowClasses = [
      classes.row,
      isSelected ? classes.rowSelected : "",
      isUnreadable ? classes.rowUnreadable : "",
    ]
      .filter(Boolean)
      .join(" ");

    function handleClick() {
      if (isFolder) {
        onToggle();
        return;
      }
      if (isUnreadable) return;
      editor.selectSnapshot(fileName);
    }

    // Indent only the name cell so the Updated + menu columns stay aligned with
    // the header. Folder Updated is max child mtime (virtual folders have none).
    const updatedLabel = formatUpdated(node.data?.mtimeMs);
    const updatedTitle = isFolder
      ? node.data?.mtimeMs
        ? "Latest file update in this folder"
        : "No files in this folder yet"
      : node.data?.mtimeMs
        ? moment(node.data.mtimeMs).format("DD.MM.YYYY HH:mm")
        : "";

    return (
      <div
        className={rowClasses}
        onClick={handleClick}
        onDoubleClick={() => isFolder && !filterTerm && setCwd(node.id)}
        // Read by the drag handler on the list below: the row the tree makes
        // draggable is the <li> around this, not this element.
        data-snapshot-name={isFolder ? undefined : fileName}
        // The drag the agent's prompt accepts starts here, where the name is
        // in hand; dragstart itself fires on the <li> the tree owns.
        onMouseDown={() => setDraggedSnapshot(isFolder ? null : fileName)}
        // And is taken back if the pointer simply came up again: mousedown
        // cannot tell a click from the start of a drag, and a click is what
        // this row is mostly for. A real drag swallows mouseup and ends in
        // dragend, which the list below clears on, so this fires only when
        // nothing was dragged at all.
        onMouseUp={() => setDraggedSnapshot(null)}
      >
        <div className={classes.rowMain} style={{ paddingLeft: `${depth * 14}px` }}>
          {editor.exportAsVersion && (
            <Checkbox
              size="small"
              style={{ padding: 0, color: "#8ab4c8", flexShrink: 0 }}
              checked={isFolder ? folderNames.length > 0 && checkedCount === folderNames.length : selectedNames.has(fileName)}
              indeterminate={isFolder && checkedCount > 0 && checkedCount < folderNames.length}
              disabled={isUnreadable || (isFolder && folderNames.length === 0)}
              inputProps={{ "aria-label": isFolder ? `Select snapshots in ${node.text}` : `Select ${node.text} for export` }}
              onClick={(event) => event.stopPropagation()}
              onChange={() => toggleExportSelection(node)}
            />
          )}
          {isFolder ? (
            isOpen ? (
              <FolderOpenIcon className={classes.icon} />
            ) : (
              <FolderIcon className={classes.icon} />
            )
          ) : isUnreadable ? (
            <Tooltip title="This file could not be parsed as JSON">
              <ReportProblemIcon className={classes.icon} />
            </Tooltip>
          ) : (
            <DescriptionIcon className={classes.icon} />
          )}

          <span className={classes.label} title={isFolder ? node.text : fileName}>
            {node.text}
          </span>

          {isFolder && node.data?.isDraft && (
            <Tooltip title="Nothing is in this folder yet, so it exists only until you reload">
              <span className={classes.meta}>empty</span>
            </Tooltip>
          )}

          {isFolder && downloadingFolderPath === folderPathFromNodeId(node.id) && (
            <Tooltip title="Preparing snapshot folder download">
              <CircularProgress size={12} thickness={6} className={classes.jobSpinner} />
            </Tooltip>
          )}

          {/* A folder carries no lock of its own — it shows one when every
              snapshot inside it is locked. */}
          {node.data?.locked && (
            <Tooltip
              title={
                isFolder
                  ? "Every snapshot in this folder is locked"
                  : "Locked — this snapshot cannot be edited"
              }
            >
              <LockIcon className={classes.lockIcon} />
            </Tooltip>
          )}

          {/* Clears itself: opening the task dialog consumes the finished job. */}
          {jobStatus && jobStatus.state === "running" && (
            <Tooltip title={`${jobStatus.noun} running — you can leave this to it`}>
              <CircularProgress size={12} thickness={6} className={classes.jobSpinner} />
            </Tooltip>
          )}
          {jobStatus && jobStatus.state === "finished" && (
            <Tooltip
              title={
                jobStatus.outcome === "error"
                  ? `${jobStatus.noun} failed — open ${jobStatus.readIn} to see why`
                  : jobStatus.outcome === "warning"
                    ? `${jobStatus.noun} finished with something to look at — open ${jobStatus.readIn}`
                    : `${jobStatus.noun} finished — open ${jobStatus.readIn} to see the result`
              }
            >
              <span
                className={`${classes.jobDot} ${
                  jobStatus.outcome === "error"
                    ? classes.jobDotError
                    : jobStatus.outcome === "warning"
                      ? classes.jobDotWarning
                      : classes.jobDotSuccess
                }`}
              />
            </Tooltip>
          )}
        </div>

        <span className={classes.updated} title={updatedTitle}>
          {updatedLabel}
        </span>

        <div className={classes.menuSlot}>
          {!isFolder && !isUnreadable && (
            <Tooltip title="Reload snapshot">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  void reloadSnapshot(node);
                }}
              >
                <RefreshIcon style={{ fontSize: "14px" }} />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setMenu({ anchorEl: event.currentTarget, node });
            }}
          >
            <MoreVertIcon style={{ fontSize: "14px" }} />
          </IconButton>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <TextField
          className={classes.filter}
          placeholder="Filter snapshots"
          value={filterTerm}
          onChange={(event) => setFilterTerm(event.target.value)}
          size="small"
          InputProps={{ className: classes.filterInput }}
        />
        <input
          id={ADD_SNAPSHOT_INPUT_ID}
          type="file"
          hidden
          accept="application/json,.json"
          onChange={(event) => {
            void chooseSnapshotFile(event.target.files?.[0]);
            // Cleared so picking the same file twice fires onChange again.
            event.target.value = "";
          }}
        />
        <Tooltip title="Add a snapshot">
          <IconButton
            size="small"
            onClick={() => document.getElementById(ADD_SNAPSHOT_INPUT_ID)?.click()}
          >
            <NoteAddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {canImportVersion && (
          <Tooltip title="Import a version as a snapshot">
            <IconButton size="small" onClick={() => setIsImportVersionOpen(true)}>
              <SystemUpdateAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="New folder">
          <IconButton size="small" onClick={() => openPrompt("createFolder")}>
            <CreateNewFolderIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reload the snapshot folder">
          <span>
            <IconButton size="small" onClick={refresh} disabled={isRefreshing}>
              {isRefreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        {/* Builds, submits and exports keep running with their dialogs closed,
            so the one place that says what is going on belongs up here. */}
        <JobsButton
          runningCount={editor.jobs.list.filter((job) => job.state === "running").length}
          onClick={() => setIsJobsOpen(true)}
        />
        {editor.exportAsVersion && (
          <Tooltip title="Export selected snapshots as versions">
            <span>
              <IconButton
                size="small"
                disabled={selectedForExport.length === 0}
                onClick={() => setVersionFileNames([...selectedForExport])}
              >
                <PublishIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </div>

      {/* The tree's own drag moves a row between folders; the same drag also
          carries the file name, so dropping it on the agent's prompt writes
          the name there rather than it being typed out. The handler sits here
          because react-dnd makes the <li> draggable, and dragstart bubbles up
          to this element rather than down to the row inside it. */}
      <div
        className={classes.treeScroll}
        onDragStart={(event) => {
          // Only a direct child: a folder's <li> holds the rows beneath it as
          // well, and those must not lend it their names.
          const row = event.target.querySelector
            ? event.target.querySelector(":scope > [data-snapshot-name]")
            : null;
          const name = (row || event.target.firstElementChild || {}).dataset?.snapshotName;
          if (name) event.dataTransfer?.setData(SNAPSHOT_DRAG_TYPE, name);
        }}
        onDragEnd={() => setDraggedSnapshot(null)}
      >
        {!editor.areSnapshotsLoaded ? (
          <Typography className={classes.empty}>Loading snapshots…</Typography>
        ) : treeData.length === 0 ? (
          <Typography className={classes.empty}>
            {filterTerm ? "No snapshot matches this filter." : "The snapshot folder is empty."}
          </Typography>
        ) : (
          <>
            <div className={classes.columnHeader}>
              {renderSortLabel("name", "Name", false)}
              {renderSortLabel("updated", "Updated", true)}
              <div className={classes.menuSlot} aria-hidden />
            </div>
            {/* The way out of a folder, at the top of the list where the eye
                already is. */}
            {!filterTerm && cwd !== ROOT_ID && (
              <div className={classes.row} onClick={() => setCwd(parentOfCwd)}>
                <div className={classes.rowMain}>
                  <ArrowUpwardIcon className={classes.icon} />
                  <span className={classes.label}>..</span>
                </div>
              </div>
            )}
            <Tree
              // Folders start collapsed, except while filtering — a filter that
              // hides its own matches inside closed folders would be useless.
              // The key remounts the tree so the open state follows the filter.
              key={filterTerm ? "filtered" : "browsing"}
              tree={treeData}
              rootId={filterTerm ? ROOT_ID : cwd}
              sort={false}
              initialOpen={Boolean(filterTerm)}
              render={renderNode}
              // Files and folders both move, and only onto folders (or the
              // root). A folder cannot land inside itself.
              canDrag={() => true}
              canDrop={(_tree, { dragSource, dropTargetId }) => {
                if (!dragSource) return false;
                const target = folderPathFromNodeId(dropTargetId);
                if (target === null) return false;
                if (!dragSource.droppable) return true;

                const source = folderPathFromNodeId(dragSource.id);
                return source !== null && !isInsideFolder(target, source);
              }}
              onDrop={handleDrop}
            />
          </>
        )}
      </div>

      <Menu
        anchorEl={menu?.anchorEl}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        PaperProps={MENU_PAPER_PROPS}
      >
        {menu?.node?.droppable
          ? [
            <MenuItem key="renameFolder" onClick={() => openPrompt("renameFolder", menu.node)}>
              Rename folder…
            </MenuItem>,
            <MenuItem key="createSubFolder" onClick={() => openPrompt("createFolder", menu.node)}>
              New folder inside…
            </MenuItem>,
            <MenuItem
              key="downloadFolder"
              disabled={menu.node.data?.isDraft || Boolean(downloadingFolderPath)}
              onClick={() => {
                const folderPath = folderPathFromNodeId(menu.node.id);
                setMenu(null);
                setDownloadingFolderPath(folderPath);
                void editor.downloadFolder(folderPath).finally(() => setDownloadingFolderPath(null));
              }}
            >
              Download folder
            </MenuItem>,
            // Only an empty folder can be dropped: one with snapshots in it goes
            // away by moving them out, which "Rename folder" to the root does.
            menu.node.data?.isDraft ? (
              <MenuItem
                key="removeFolder"
                onClick={() => removeDraftFolder(folderPathFromNodeId(menu.node.id))}
              >
                Remove folder
              </MenuItem>
            ) : null,
            // Applies to every snapshot underneath, nested folders included.
            menu.node.data?.isDraft ? null : (
              <MenuItem key="folderLock" onClick={() => setFolderLock(menu.node)}>
                {menu.node.data?.locked ? "Unlock folder" : "Lock folder"}
              </MenuItem>
            ),
            // Preview and delivery both live in one dialog: they are two steps
            // of the same loop, and delivery only makes sense after a preview.
            menu.node.data?.isDraft || !editor.taskDelivery ? null : (
              <MenuItem
                key="task"
                onClick={() => {
                  setTaskFolderPath(folderPathFromNodeId(menu.node.id));
                  setMenu(null);
                }}
              >
                Build / submit task…
              </MenuItem>
            ),
          ].filter(Boolean)
          : [
            // `menu` is null whenever the menu is closed, and this branch is
            // still rendered then — so anything read during render (rather than
            // inside an onClick) has to go through the optional chain.
            <MenuItem key="lock" onClick={() => setLock(menu?.node)}>
              {menu?.node?.data?.locked ? "Unlock" : "Lock"}
            </MenuItem>,
            <MenuItem key="rename" onClick={() => openPrompt("rename", menu.node)}>
              Rename…
            </MenuItem>,
            <MenuItem key="duplicate" onClick={() => openPrompt("duplicate", menu.node)}>
              Duplicate…
            </MenuItem>,
            // Downloads the file as it is on disk — the same JSON that would be
            // added back by "Add snapshot" above the tree.
            <MenuItem
              key="download"
              onClick={() => {
                const fileName = fileNameFromNodeId(menu?.node?.id);
                setMenu(null);
                void editor.downloadSnapshot(fileName);
              }}
            >
              Download
            </MenuItem>,
            // No "Move to folder…": dragging a snapshot onto a folder does the
            // same thing (handleDrop → setFolder) and shows you where it lands.
            // Discarding a locked snapshot is refused by the server, so do not
            // offer it — renaming and moving stay available, they are filing.
            menu?.node?.data?.locked ? null : (
              <MenuItem key="discard" onClick={() => requestDiscard(menu?.node)}>
                Discard
              </MenuItem>
            ),
          ].filter(Boolean)}
      </Menu>

      <Dialog
        open={Boolean(prompt) && prompt?.kind === "discard"}
        onClose={() => setPrompt(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={DIALOG_PAPER_PROPS}
      >
        {prompt?.kind === "discard" && (
          <>
            <DialogTitle>Discard snapshot?</DialogTitle>
            <DialogContent>
              <Typography>
                “{String(fileNameFromNodeId(prompt.node.id) || "").replace(/\.json$/i, "")}” will be
                removed from the library and moved to the badones folder. It is not deleted permanently.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPrompt(null)}>Cancel</Button>
              <Button color="secondary" onClick={confirmDiscard}>
                Discard
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={Boolean(prompt) && prompt?.kind !== "discard"}
        onClose={() => setPrompt(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={DIALOG_PAPER_PROPS}
      >
        {prompt && prompt.kind !== "discard" && (
          <>
            <DialogTitle>{PROMPT_LABELS[prompt.kind].title}</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                fullWidth
                label={PROMPT_LABELS[prompt.kind].label}
                helperText={PROMPT_LABELS[prompt.kind].help}
                value={prompt.value}
                onChange={(event) => setPrompt({ ...prompt, value: event.target.value })}
                onKeyDown={(event) => {
                  if (event.key === "Enter") submitPrompt();
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPrompt(null)}>Cancel</Button>
              <Button color="primary" onClick={submitPrompt}>
                Apply
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <DrawerJobs
        open={isJobsOpen}
        jobs={editor.jobs.list}
        progressOf={editor.jobs.progressOf}
        onOpenTask={setTaskFolderPath}
        onClose={() => setIsJobsOpen(false)}
      />

      <DialogDeliveryTask
        editor={editor}
        open={editor.taskDelivery && Boolean(taskFolderPath)}
        folderPath={taskFolderPath}
        onClose={() => setTaskFolderPath(null)}
        createSnackbar={createSnackbar}
      />

      <DialogExportAsVersion
        editor={editor}
        open={editor.exportAsVersion && Boolean(versionFileNames)}
        fileNames={versionFileNames || []}
        onClose={() => setVersionFileNames(null)}
        onFileExported={(fileName) =>
          setSelectedForExport((current) => current.filter((name) => name !== fileName))
        }
        onExported={() => setSelectedForExport([])}
        createSnackbar={createSnackbar}
      />

      <DialogImportVersion
        editor={editor}
        open={isImportVersionOpen}
        onClose={() => setIsImportVersionOpen(false)}
        createSnackbar={createSnackbar}
        draftFolders={draftFolders}
        // A file is in it now, so it is a real folder: let go of the draft, the
        // same as when a snapshot is dropped into one.
        onImported={(folderPath) =>
          setDraftFolders((current) => current.filter((path) => path !== folderPath))
        }
      />
    </div>
  );
}
